import  ActionType from '../../store/ActionType';
import { GlobalState, GlobalDispatch } from '../app/AppWrapper';
import OlStyle from 'ol/style/Style';
import OlStroke from 'ol/style/Stroke';
import OlText from 'ol/style/Text';
import OlFill from 'ol/style/Fill';
import OlCircle from 'ol/style/Circle';
import OlMultipoint from 'ol/geom/MultiPoint';
import OlLayerVector from 'ol/layer/Vector';
import OlSourceVector from 'ol/source/Vector';
import OlFormatGeoJSON from 'ol/format/GeoJSON';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import * as olProj from 'ol/proj';
import * as olExtent from 'ol/extent';
import { MeshBoundaries, MeshTileGridConfig } from '../../apis/meshApi';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import TileGrid from 'ol/tilegrid/TileGrid';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import GeoJSON from 'ol/format/GeoJSON.js';

import Api from '../../apis/meshApi';
import { dataLinkBackendUrl } from '../../apis/support';

let vector;
let tileVector;

export const colors = [
  '#008BEC',
  '#F9BA32',
  '#10E7DC',
  '#FE7860',
  '#AB8BE5',
  '#2EB67D',
  '#FCE51E',
  '#60BFF5',
  '#69426A',
  '#187206',
];

interface Topology extends GeoJSON.FeatureCollection {
  Projection: string;
  ProjectionWkt: string;
  ProjectionShortName: string;
}
export const showMeshOnMap = (
  globalState: GlobalState,
  globalDispatch: GlobalDispatch,
  // Changed param to `meshData: MeshBoundaries` iso `meshResp: GetMeshBoundariesResponse`:
  // meshResp: GetMeshBoundariesResponse, //['_response']
  meshData: MeshBoundaries
) => {
  // console.log('showMeshOnMap is being called.');

  if (vector) {
    //Clear Feature Boundaries and remove layer
    vector.getSource().clear();
    (globalState.map as any).olMap.removeLayer(vector);
  }

  // const meshData = meshResp['data'] as MeshBoundaries; // <= avoid TS error
  const readProjection: Topology = JSON.parse(meshData);
  proj4.defs(readProjection.ProjectionWkt, readProjection.Projection);
  register(proj4);

  // new OlFormatGeoJSON({ layerName: 'layer' })
  const featuresTOPOJson = new OlFormatGeoJSON().readFeatures(meshData, {
    dataProjection: readProjection.ProjectionWkt,
    featureProjection: (globalState.map as any).olMap
      .getView()
      .getProjection()
      .getCode(),
  });

  const features = [];

  featuresTOPOJson.forEach(feature => {
    if (!feature.get('name').includes('LandBoundary')) {
      const boundaryNum = feature.get('name').split('_')[1];
      feature.set('Color', colors[boundaryNum % colors.length]);

      // Whether a feature is checked, or not. Should be highligthed if selected
      feature.set('Selected', false);
      features.push(feature);
    }
  });

  // store.dispatch(setFeatures(features));
  globalDispatch({ type: ActionType.SET_MESH_FEATURES, data: features });
  globalDispatch({ type: ActionType.SET_MESH_PROJECTION, data: readProjection.Projection });
  globalDispatch({ type: ActionType.SET_MESH_PROJECTION_WKT, data: readProjection.ProjectionWkt });
  globalDispatch({
    type: ActionType.SET_MESH_PROJECTION_SHORTNAME,
    data: readProjection.ProjectionShortName,
  });

  const MeshStyle = (feature, resolution) => {
    const zoom = (globalState.map as any).olMap.getView().getZoom();
    const dsize = (2 / resolution) * zoom;
    const size = Math.round(dsize);
    let newSize = 0;

    if (zoom > 10) {
      newSize = size;
    }

    if (feature.get('name').includes('LandBoundary')) {
      return new OlStyle({
        stroke: new OlStroke({
          color: '#0076C8',
          width: 2,
        }),
      });
    } else {
      const boundaryStyle = [
        new OlStyle({
          stroke: new OlStroke({
            color: feature.get('Color'),
            width: 2,
          }),
          text: new OlText({
            textAlign: 'center',
            textBaseline: 'top',
            font: '12px Roboto',
            fill: new OlFill({
              color: '#fff',
            }),
            stroke: new OlStroke({
              color: '#000',
              width: 3,
            }),
          }),
        }),
        new OlStyle({
          image: new OlCircle({
            radius: newSize <= 0 ? 0 : 1,
            fill: new OlFill({
              color: 'transparent',
            }),
            stroke: new OlStroke({
              color: feature.get('Color'),
              width: 5,
              lineCap: 'butt',
            }),
          }),
          geometry: function(feature) {
            const coords = (feature.getGeometry() as any).getCoordinates();
            return new OlMultipoint(coords);
          },
        }),
      ];
      return boundaryStyle;
    }
  };

  vector = new OlLayerVector({
    visible: true,
    zIndex: 1,    
    source: new OlSourceVector({
      features: featuresTOPOJson,
      overlaps: false,
    }),
    style: MeshStyle,
  });
  vector.set('name','meshBoundaries');
  validateExtentOfMesh((globalState.map as any).olMap, globalDispatch);

  // console.log('right before adding layer, the map: ', globalState.map.olMap);
  (globalState.map as any).olMap.addLayer(vector);
};

export function zoomToMeshExtent(olMap) {
  if (vector) {
    const vectorSourceExtent = vector.getSource().getExtent();
    const vectorSourceExtentCenter = [
      vectorSourceExtent[0] + vectorSourceExtent[2] / 2,
      vectorSourceExtent[1] + vectorSourceExtent[3] / 2,
    ];
    olMap.getView().setCenter(vectorSourceExtentCenter);
    const mapSize = olMap.getSize();
    olMap.getView().fit(vectorSourceExtent, { size: [0.8 * mapSize[0], 0.8 * mapSize[1]] });
  }
}

export function calcDelta(x: number) {
  switch (true) {
    case x > 50 && x < 100:
      return 8;
      break;
    case x > 100 && x < 200:
      return 16;
      break;
    case x > 200 && x < 300:
      return 66;
      break;
    case x >= 300:
      return 100;
      break;
    default:
      return x;
      break;
  }
}

export function createFeature(input, fromProj, toProj) {
  return new Feature({
    geometry: new LineString(input).transform(fromProj, toProj),
  });
}

export function createGridStyle(color) {
  return new OlStyle({
    stroke: new OlStroke({ color: color, width: 1 }),
  });
}

export const validateExtentOfMesh = (olMap, globalDispatch) => {
  const worldExtent = olProj
    .get(
      olMap
        .getView()
        .getProjection()
        .getCode()
    )
    .getExtent();
  const meshExtent = vector.getSource().getExtent();
  const compairExtent = olExtent.getIntersection(worldExtent, meshExtent);

  if (
    isFinite(compairExtent[0]) == false ||
    isFinite(compairExtent[1]) == false ||
    isFinite(compairExtent[2]) == false ||
    isFinite(compairExtent[3]) == false
  ) {
    vector.getSource().clear();
    olMap.getView().fit(worldExtent);
    globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });
    globalDispatch({ type: ActionType.SET_MESSAGE, message: 'Mesh projection not support.' });
    globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
  } else {
    zoomToMeshExtent(olMap);
  }
};

export const cleanUpMeshTiles = (globalState: GlobalState) => {
  if (tileVector) {
    tileVector.getSource().clear();
    (globalState.map as any).olMap.removeLayer(tileVector);
  }
};

export const cleanUpMeshBoundaries = (globalState: GlobalState) => {
  if (vector) {
    //Clear Feature Boundaries and remove layer
    vector.getSource().clear();
    (globalState.map as any).olMap.removeLayer(vector);
  }
};

export const showMeshTiles = (
  globalState: GlobalState,
  _globalDispatch: GlobalDispatch,
  meshTileGridConfig: MeshTileGridConfig,
  reproject: boolean
) => {
  // cleanUpMeshTiles(globalState);

  const extent = [
    meshTileGridConfig['XMin'],
    meshTileGridConfig['YMin'],
    meshTileGridConfig['XMax'],
    meshTileGridConfig['YMax'],
  ];

  const tileGrid = new TileGrid({
    extent: extent,
    resolutions: meshTileGridConfig['Resolutions'],
    minZoom: meshTileGridConfig['MinZoom'],
  });

  const meshTileStyle = {
    Original: new OlStyle({
      stroke: new OlStroke({
        color: '#3399CC',
        width: 1.25,
      }),
    }),
    Merged: new OlStyle({
      fill: new OlFill({
        color: 'rgba(51, 153, 204, 0.5)',
      }),
      stroke: new OlStroke({
        color: '#3399CC',
        width: 1.25,
      }),
    }),
  };

  async function tileLoadFunction(tile, url) {
    const response = await Api.meshTileLoadGet(tile, url);
    if (response.data.length !== 0) {
      const featuresJson = new GeoJSON().readFeatures(response.data, {});
      tile.setFeatures(featuresJson);
    } else {
      tile.setFeatures([]);
    }
  }

  const dlUrl = dataLinkBackendUrl +
  '/api/Mesh/Tiles/Tile?projectGuid=' +
  globalState.project.currentProject.projectGuid +
  '&meshName=' +
  globalState.mesh.meshName +
  '&reproject=' +
  reproject +
  '&tilematrix={z}&tilecol={x}&tilerow={y}';

  const code = (globalState.map as any).olMap.getView().getProjection().getCode();

  const tileVector = new VectorTileLayer({ 
    declutter: true,
    source: new VectorTileSource({
      maxZoom: meshTileGridConfig['MaxZoom'],
      minZoom: meshTileGridConfig['MinZoom'],
      url: dlUrl,
      tileLoadFunction: tileLoadFunction,
      tileGrid: tileGrid,
      projection: code,
    }),    
   style: feature => {
      const type = feature.get('Type');
      return meshTileStyle[type];
    },
  });
  tileVector.set('name', 'meshtilefeatures'); 

  (globalState.map as any).olMap.addLayer(tileVector);
};
